import { useEffect, useState } from "react";
import { Check, X, Save } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { BigButton, Field, inputCls } from "@/components/ui-kit";

const card = "rounded-xl bg-white border border-border p-5 card-soft";

export function RequestsPanel({ onChanged }) {
  const { t, lang } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/requests").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (id, action) => {
    try { await api.post(`/admin/requests/${id}`, { action }); toast.success(t("plan_updated")); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={card} data-testid="admin-requests-panel">
      <h3 className="font-heading text-lg mb-2">{t("plan_requests")}</h3>
      {!list.length && <p className="text-sm text-muted-foreground">{t("no_requests")}</p>}
      <table className="w-full text-sm"><tbody>
        {list.map((r) => (
          <tr key={r.id} className="border-t border-border" data-testid="admin-request-row">
            <td className="py-2 pe-3 whitespace-nowrap">{fmtDate(r.created_at, lang)}</td>
            <td className="py-2 pe-3 font-semibold">{r.shop_name}<div className="text-xs text-muted-foreground" dir="ltr">{r.owner_email}</div></td>
            <td className="py-2 pe-3 font-bold">{t(`plan_${r.plan}`)} × {r.months}</td>
            <td className="py-2 pe-3">{t(`method_${r.method}`)}<div className="text-xs text-muted-foreground">{r.reference}</div></td>
            <td className="py-2">
              {r.status === "pending" ? (
                <span className="flex gap-1.5">
                  <button type="button" onClick={() => decide(r.id, "approve")} className="h-9 px-3 rounded-lg bg-[#1F9D55] text-white font-bold flex items-center gap-1" data-testid="admin-request-approve"><Check className="w-4 h-4" />{t("approve")}</button>
                  <button type="button" onClick={() => decide(r.id, "reject")} className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive font-bold flex items-center gap-1" data-testid="admin-request-reject"><X className="w-4 h-4" />{t("reject")}</button>
                </span>
              ) : <span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-bold" data-testid="admin-request-status">{t(`req_${r.status}`)}</span>}
            </td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

export function PlatformSettings() {
  const { t } = useApp();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setF({ pro: r.data.prices?.pro ?? "", business: r.data.prices?.business ?? "", currency: r.data.currency || "", instructions: r.data.instructions || "" })).catch(() => {}); }, []);
  if (!f) return null;
  const num = (v) => (v === "" ? null : Number(v));
  const save = async () => {
    try { await api.put("/admin/settings", { prices: { pro: num(f.pro), business: num(f.business) }, currency: f.currency, instructions: f.instructions }); toast.success(t("saved")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`${card} space-y-3`} data-testid="admin-platform-settings">
      <h3 className="font-heading text-lg">{t("platform_settings")}</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label={t("price_pro")}><input type="number" min={0} value={f.pro} onChange={(e) => setF({ ...f, pro: e.target.value })} className={inputCls} data-testid="admin-price-pro" /></Field>
        <Field label={t("price_business")}><input type="number" min={0} value={f.business} onChange={(e) => setF({ ...f, business: e.target.value })} className={inputCls} data-testid="admin-price-business" /></Field>
        <Field label={t("currency")}><input value={f.currency} maxLength={10} onChange={(e) => setF({ ...f, currency: e.target.value })} className={inputCls} data-testid="admin-price-currency" /></Field>
      </div>
      <Field label={t("instructions_label")}>
        <textarea rows={4} value={f.instructions} maxLength={1500} onChange={(e) => setF({ ...f, instructions: e.target.value })} className={`${inputCls} h-auto py-2`} placeholder={t("pay_instructions_default")} data-testid="admin-instructions-input" />
      </Field>
      <BigButton onClick={save} data-testid="admin-settings-save"><Save className="w-5 h-5" />{t("save")}</BigButton>
    </div>
  );
}
